import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  addDoc,
  serverTimestamp,
  increment,
  runTransaction
} from 'firebase/firestore';
import { deleteUser as deleteAuthUser, getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../config/firebase';
import { NotificationService } from './notificationService';
import { TransactionService } from './transactionService';
import { UserManagementService } from './userManagementService';

// Service pour gérer les utilisateurs
export class UserService {
  static async getDerivedCreditBalance(userId) {
    if (!userId) {
      return null;
    }

    try {
      return await TransactionService.getDerivedUserCreditBalance(userId, { includeLegacy: true });
    } catch (error) {
      console.error('Erreur lors du recalcul du crédit utilisateur:', error);
      return null;
    }
  }
  
  // Récupérer tous les utilisateurs avec vérification de cohérence
  static async getAllUsers() {
    try {
      console.log('Récupération des utilisateurs...');
      
      // Récupérer tous les utilisateurs de Firestore
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const firestoreUsers = [];
      
      usersSnapshot.forEach((doc) => {
        firestoreUsers.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log(`${firestoreUsers.length} utilisateurs trouvés dans Firestore`);
      
      // Vérifier la cohérence avec Firebase Auth (côté client)
      const authInstance = getAuth();
      const validUsers = [];
      
      for (const user of firestoreUsers) {
        try {
          // Vérifier si l'utilisateur existe toujours dans Auth
          // Note: Cette vérification est limitée côté client
          // On garde tous les utilisateurs pour l'instant
          validUsers.push(user);
        } catch (error) {
          console.warn(`Utilisateur ${user.id} pourrait être orphelin:`, error);
          // On garde quand même l'utilisateur pour éviter les suppressions accidentelles
          validUsers.push(user);
        }
      }

      return validUsers;
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      
      // Créer une alerte système pour l'admin
      try {
        console.warn('Service d\'alerte non disponible - Erreur de base de données lors de la récupération des utilisateurs');
      } catch (alertError) {
        console.error('Erreur lors de la création de l\'alerte:', alertError);
      }
      
      throw error;
    }
  }
  
  // Récupérer les revendeurs en attente d'activation
  static async getPendingResellers() {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'reseller'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(usersQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des revendeurs en attente:', error);
      
      // Créer une alerte système pour l'admin
      try {
        console.warn('Service d\'alerte non disponible - Erreur de base de données lors de la récupération des revendeurs en attente');
      } catch (alertError) {
        console.error('Erreur lors de la création de l\'alerte:', alertError);
      }
      
      throw error;
    }
  }
  
  // Activer un compte revendeur
  static async activateReseller(userId, adminId) {
    try {
      void adminId;

      const activateResellerAccount = httpsCallable(functions, 'activateResellerAccount');
      const result = await activateResellerAccount({ targetUserId: userId });

      return Boolean(result?.data?.success);
    } catch (error) {
      console.error('Erreur lors de l\'activation du revendeur:', error);
      throw new Error(error.message || 'Erreur lors de l\'activation du revendeur');
    }
  }
  
  // Désactiver un compte revendeur
  static async deactivateReseller(userId, adminId, reason = '') {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('Utilisateur introuvable');
      }
      
      await updateDoc(userRef, {
        status: 'inactive',
        deactivatedBy: adminId,
        deactivatedAt: serverTimestamp(),
        deactivationReason: reason,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la désactivation du revendeur:', error);
      throw error;
    }
  }
  
  // Mettre à jour le profil utilisateur
  static async updateUserProfile(userId, updates) {
    try {
      const userRef = doc(db, 'users', userId);
      
      // Filtrer les champs autorisés pour la mise à jour
      const allowedFields = [
        'firstName', 'lastName', 'phone', 'company', 
        'address', 'city', 'postalCode'
      ];
      
      const filteredUpdates = Object.keys(updates)
        .filter(key => allowedFields.includes(key))
        .reduce((obj, key) => {
          obj[key] = updates[key];
          return obj;
        }, {});
      
      await updateDoc(userRef, {
        ...filteredUpdates,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      throw error;
    }
  }
  
  // Récupérer le profil d'un utilisateur
  static async getUserProfile(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        throw new Error('Utilisateur introuvable');
      }
      
      return {
        id: userId,
        ...userDoc.data()
      };
    } catch (error) {
      console.error('Erreur lors de la récupération du profil:', error);
      throw error;
    }
  }
  
  // Récupérer les statistiques des utilisateurs
  static async getUsersStats() {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => doc.data());
      
      const stats = {
        total: users.length,
        admins: users.filter(u => u.role === 'admin').length,
        resellers: users.filter(u => u.role === 'reseller').length,
        active: users.filter(u => u.status === 'active').length,
        pending: users.filter(u => u.status === 'pending').length,
        inactive: users.filter(u => u.status === 'inactive').length
      };
      
      return stats;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  }
  
  // Ajuster le solde d'un utilisateur (sécurisé)
  static async rechargeUserBalance(userId, amount, reason, adminId, options = {}) {
    // Validations de sécurité
    if (!userId || !adminId) {
      throw new Error('ID utilisateur et admin requis');
    }
    
    if (!amount || typeof amount !== 'number' || amount <= 0) {
      throw new Error('Montant invalide - doit être un nombre positif');
    }
    
    if (amount > 1000000) { // Limite de sécurité : 1M DZD
      throw new Error('Montant trop élevé - maximum 1,000,000 DZD par recharge');
    }
    
    // Arrondir à 2 décimales pour éviter les problèmes de précision
    const roundedAmount = Math.round(amount * 100) / 100;
    const movementType = ['credit', 'payment', 'cash'].includes(options.movementType)
      ? options.movementType
      : 'credit';
    const direction = options.direction === 'debit' ? 'debit' : 'credit';
    const methodLabel = movementType === 'payment'
      ? 'Paiement'
      : movementType === 'cash'
        ? 'Espèces'
        : 'Crédit';
    const isCreditMovement = movementType === 'credit';
    const reasonText = reason || (direction === 'debit'
      ? `Récupération ${methodLabel.toLowerCase()} par admin`
      : `${methodLabel} ajouté par admin`);
    const derivedCreditBalance = isCreditMovement
      ? await this.getDerivedCreditBalance(userId)
      : null;
    
    try {
      const rechargeResult = await runTransaction(db, async (transaction) => {
        // Vérifier l'utilisateur
        const userRef = doc(db, 'users', userId);
        const userDoc = await transaction.get(userRef);
        
        if (!userDoc.exists()) {
          throw new Error('Utilisateur introuvable');
        }
        
        const userData = userDoc.data();
        
        // Vérifier que l'utilisateur est un revendeur actif
        if (userData.role !== 'reseller') {
          throw new Error('Seuls les revendeurs peuvent recevoir des recharges');
        }
        
        if (userData.status !== 'active') {
          throw new Error('Le compte doit être activé pour recevoir des recharges');
        }
        
        // Vérifier l'admin
        const adminRef = doc(db, 'users', adminId);
        const adminDoc = await transaction.get(adminRef);
        
        if (!adminDoc.exists() || adminDoc.data().role !== 'admin') {
          throw new Error('Seuls les administrateurs peuvent effectuer des recharges');
        }
        
        const currentBalance = Number(userData.balance || 0);
        const storedCreditBalance = Number(userData.creditBalance || 0);
        const shouldAffectBalance = direction === 'credit';
        const shouldAffectCreditBalance = isCreditMovement || direction === 'debit';
        const currentCreditBalance = shouldAffectCreditBalance
          ? TransactionService.resolveEffectiveCreditBalance(userData, derivedCreditBalance, { allowBalanceFallback: true })
          : storedCreditBalance;

        const newBalance = shouldAffectBalance
          ? (direction === 'debit' ? currentBalance - roundedAmount : currentBalance + roundedAmount)
          : currentBalance;
        const newCreditBalance = shouldAffectCreditBalance
          ? (direction === 'debit' ? currentCreditBalance - roundedAmount : currentCreditBalance + roundedAmount)
          : currentCreditBalance;
        
        if (shouldAffectBalance && direction === 'debit' && currentBalance < roundedAmount) {
          throw new Error('Solde insuffisant pour effectuer cette déduction');
        }

        if (shouldAffectCreditBalance && direction === 'debit' && currentCreditBalance < roundedAmount) {
          throw new Error('Crédit insuffisant pour effectuer cette récupération');
        }
        
        // Vérifier la limite de solde (sécurité)
        if (newBalance > 10000000) { // Limite : 10M DZD
          throw new Error('Solde maximum atteint (10,000,000 DZD)');
        }

        if (newCreditBalance > 10000000) {
          throw new Error('Crédit maximum atteint (10,000,000 DZD)');
        }
        
        // Mettre à jour le solde utilisateur
        const userUpdateData = {
          balance: newBalance,
          creditBalance: newCreditBalance,
          updatedAt: serverTimestamp()
        };

        if (direction === 'credit') {
          userUpdateData.lastRecharge = serverTimestamp();
        }

        transaction.update(userRef, userUpdateData);
        
        // Créer l'entrée de transaction
        const transactionRef = doc(collection(db, 'transactions'));
        transaction.set(transactionRef, {
          userId,
          type: 'recharge',
          amount: roundedAmount,
          method: movementType,
          direction,
          adminId,
          reason: reasonText,
          balanceBefore: currentBalance,
          balanceAfter: newBalance,
          creditBalanceBefore: currentCreditBalance,
          creditBalanceAfter: newCreditBalance,
          status: 'completed',
          createdAt: serverTimestamp(),
          metadata: {
            adminEmail: adminDoc.data().email,
            userEmail: userData.email,
            ipAddress: null, // À implémenter si nécessaire
            userAgent: null  // À implémenter si nécessaire
          }
        });
        
        // Créer l'entrée dans le ledger pour audit
        const ledgerRef = doc(collection(db, 'ledger'));
        transaction.set(ledgerRef, {
          userId,
          type: direction === 'debit' ? 'debit' : 'credit',
          amount: roundedAmount,
          description: `${direction === 'debit' ? 'Récupération' : 'Ajout'} ${methodLabel.toLowerCase()} - ${reasonText}`,
          adminId,
          transactionId: transactionRef.id,
          balanceBefore: currentBalance,
          balanceAfter: newBalance,
          creditBalanceBefore: currentCreditBalance,
          creditBalanceAfter: newCreditBalance,
          createdAt: serverTimestamp(),
          metadata: {
            source: 'admin_balance_adjustment',
            validated: true,
            movementType,
            direction,
            affectsBalance: shouldAffectBalance,
            affectsCreditBalance: shouldAffectCreditBalance
          }
        });
        
        return {
          success: true,
          newBalance,
          newCreditBalance,
          transactionId: transactionRef.id,
          amount: roundedAmount,
          affectsBalance: shouldAffectBalance
        };
      });

      try {
        if (direction === 'debit') {
          await NotificationService.notifyBalanceDebited(userId, roundedAmount, 'admin_credit');
        } else {
          await NotificationService.notifyBalanceCredited(userId, roundedAmount, `admin_${movementType}`);
        }
      } catch (notificationError) {
        console.error('Erreur lors de la création de la notification de recharge:', notificationError);
      }

      return rechargeResult;
    } catch (error) {
      console.error('Erreur lors de la recharge:', error);
      throw error;
    }
  }
  
  // Vérifier le solde d'un utilisateur (sécurisé)
  static async getUserBalance(userId) {
    try {
      if (!userId) {
        throw new Error('ID utilisateur requis');
      }
      
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        throw new Error('Utilisateur introuvable');
      }
      
      const balance = userDoc.data().balance || 0;
      
      // Vérification de cohérence (le solde ne peut pas être négatif)
      if (balance < 0) {
        console.error(`Solde négatif détecté pour l'utilisateur ${userId}: ${balance}`);
        // Optionnel: corriger automatiquement ou alerter
      }
      
      return Math.round(balance * 100) / 100; // Arrondir à 2 décimales
    } catch (error) {
      console.error('Erreur lors de la vérification du solde:', error);
      throw error;
    }
  }
  
  // Débiter le solde (utilisé lors des achats)
  static async debitUserBalance(userId, amount, transactionId, description = '') {
    if (!userId || !transactionId) {
      throw new Error('ID utilisateur et transaction requis');
    }
    
    if (!amount || typeof amount !== 'number' || amount <= 0) {
      throw new Error('Montant invalide');
    }
    
    const roundedAmount = Math.round(amount * 100) / 100;
    
    try {
      return await runTransaction(db, async (transaction) => {
        const userRef = doc(db, 'users', userId);
        const userDoc = await transaction.get(userRef);
        
        if (!userDoc.exists()) {
          throw new Error('Utilisateur introuvable');
        }
        
        const userData = userDoc.data();
        const currentBalance = userData.balance || 0;
        
        if (currentBalance < roundedAmount) {
          throw new Error('Solde insuffisant');
        }
        
        const newBalance = currentBalance - roundedAmount;
        
        // Mettre à jour le solde
        transaction.update(userRef, {
          balance: newBalance,
          updatedAt: serverTimestamp()
        });
        
        // Créer l'entrée dans le ledger
        const ledgerRef = doc(collection(db, 'ledger'));
        transaction.set(ledgerRef, {
          userId,
          type: 'debit',
          amount: roundedAmount,
          description: description || 'Achat de produit',
          transactionId,
          balanceBefore: currentBalance,
          balanceAfter: newBalance,
          createdAt: serverTimestamp(),
          metadata: {
            source: 'purchase',
            validated: true
          }
        });
        
        return {
          success: true,
          newBalance,
          amount: roundedAmount
        };
      });
    } catch (error) {
      console.error('Erreur lors du débit:', error);
      
      // Créer une alerte pour l'erreur de débit
      try {
        console.warn(`Service d'alerte non disponible - Erreur de débit pour l'utilisateur ${userId}: ${error.message}`);
      } catch (alertError) {
        console.error('Erreur lors de la création de l\'alerte:', alertError);
      }
      
      throw error;
    }
  }

  // Récupérer le nom d'un admin par son ID
  static async getAdminName(adminId) {
    try {
      if (!adminId) {
        return 'Administrateur inconnu';
      }

      const getAdminPublicName = httpsCallable(functions, 'getAdminPublicName');
      const response = await getAdminPublicName({ adminId });
      const resolvedName = String(response?.data?.name || '').trim();

      return resolvedName || 'Administrateur';
    } catch (error) {
      console.error('Erreur lors de la récupération du nom de l\'admin:', error);
      return 'Administrateur';
    }
  }

  // Rechercher des utilisateurs
  static async searchUsers(searchTerm, role = null) {
    try {
      let usersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc')
      );
      
      if (role) {
        usersQuery = query(
          collection(db, 'users'),
          where('role', '==', role),
          orderBy('createdAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(usersQuery);
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filtrer côté client (Firebase ne supporte pas la recherche textuelle native)
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return users.filter(user => 
          user.email?.toLowerCase().includes(term) ||
          user.firstName?.toLowerCase().includes(term) ||
          user.lastName?.toLowerCase().includes(term) ||
          user.company?.toLowerCase().includes(term)
        );
      }
      
      return users;
    } catch (error) {
      console.error('Erreur lors de la recherche d\'utilisateurs:', error);
      throw error;
    }
  }

  // Rechercher spécifiquement les revendeurs actifs
  static async searchResellers(searchTerm) {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'reseller'),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(usersQuery);
      const resellers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filtrer côté client pour la recherche textuelle
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return resellers.filter(user => 
          user.email?.toLowerCase().includes(term) ||
          user.firstName?.toLowerCase().includes(term) ||
          user.lastName?.toLowerCase().includes(term) ||
          user.company?.toLowerCase().includes(term)
        );
      }
      
      return resellers;
    } catch (error) {
      console.error('Erreur lors de la recherche de revendeurs:', error);
      throw error;
    }
  }

  // Supprimer un utilisateur (Firestore seulement - Auth nécessite des privilèges admin)
  static async deleteUser(userId) {
    try {
      return await UserManagementService.deleteUser(userId);
    } catch (error) {
       console.error('Erreur lors de la suppression de l\'utilisateur:', error);
       console.warn('Service d\'alerte non disponible - Erreur lors de la suppression de l\'utilisateur');
       throw error;
     }
  }

  // Synchroniser les utilisateurs (nettoyer les données incohérentes)
  static async syncUsers() {
    try {
      return await UserManagementService.syncUsers();
    } catch (error) {
       console.error('Erreur lors de la synchronisation des utilisateurs:', error);
       console.warn('Service d\'alerte non disponible - Erreur lors de la synchronisation des utilisateurs');
       throw error;
     }
  }
}

export default UserService;
